import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const PURPOSES = [
  ["chat", "Conversas", "Usada pelos agentes e pelo simulador."],
  ["embedding", "Embeddings", "Usada para indexar e consultar bases de conhecimento."],
  ["transcription", "Transcrição", "Usada para transformar áudios em texto."],
  ["speech", "Síntese de voz", "Usada para gerar respostas em áudio."],
];

const PROVIDERS = {
  openai: "OpenAI",
  google: "Google",
  azure_speech: "Azure Speech",
};

const PROVIDERS_BY_PURPOSE = {
  chat: ["openai", "google"],
  embedding: ["google", "openai"],
  transcription: ["openai", "google"],
  speech: ["openai", "google", "azure_speech"],
};

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 18 },
  content: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
  intro: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    backgroundColor: theme.palette.action.hover,
  },
  form: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  secret: { gridColumn: "1 / -1" },
  list: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },
  credential: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  credentialInfo: { minWidth: 0 },
  empty: { padding: theme.spacing(2), textAlign: "center" },
}));

const purposeLabel = (purpose) =>
  PURPOSES.find(([value]) => value === purpose)?.[1] || purpose;

const AiCredentialsModal = ({
  open,
  onClose,
  onSaved,
  initialPurpose = "chat",
  initialProvider,
}) => {
  const classes = useStyles();
  const [credentials, setCredentials] = useState([]);
  const [purpose, setPurpose] = useState("chat");
  const [provider, setProvider] = useState("openai");
  const [secret, setSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const availableProviders = useMemo(
    () => PROVIDERS_BY_PURPOSE[purpose] || [],
    [purpose]
  );

  const loadCredentials = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/credentials");
      const next = data || [];
      setCredentials(next);
      return next;
    } catch (error) {
      toastError(error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const nextPurpose = PROVIDERS_BY_PURPOSE[initialPurpose] ? initialPurpose : "chat";
    const nextProviders = PROVIDERS_BY_PURPOSE[nextPurpose];
    setPurpose(nextPurpose);
    setProvider(nextProviders.includes(initialProvider) ? initialProvider : nextProviders[0]);
    setSecret("");
    setShowSecret(false);
    loadCredentials();
  }, [initialProvider, initialPurpose, loadCredentials, open]);

  const close = () => {
    if (saving) return;
    setSecret("");
    setShowSecret(false);
    onClose();
  };

  const changePurpose = (event) => {
    const nextPurpose = event.target.value;
    const nextProviders = PROVIDERS_BY_PURPOSE[nextPurpose] || [];
    setPurpose(nextPurpose);
    setProvider((current) =>
      nextProviders.includes(current) ? current : nextProviders[0]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/ai/credentials/${provider}`, { purpose, secret });
      setSecret("");
      setShowSecret(false);
      const next = await loadCredentials();
      toast.success("Credencial de IA armazenada com segurança.");
      if (onSaved) onSaved(next);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.paper }}
    >
      <DialogTitle>Credenciais de IA</DialogTitle>
      <DialogContent dividers>
        <div className={classes.content}>
          <Typography variant="body2" color="textSecondary" className={classes.intro}>
            A chave fica criptografada e isolada neste tenant. Ela não será exibida novamente. Salvar o mesmo provedor e finalidade substitui a credencial anterior.
          </Typography>

          <div className={classes.form}>
            <TextField
              select
              variant="outlined"
              label="Finalidade"
              value={purpose}
              onChange={changePurpose}
            >
              {PURPOSES.map(([value, label, description]) => (
                <MenuItem key={value} value={value}>
                  <Box>
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="caption" color="textSecondary">{description}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              variant="outlined"
              label="Provedor"
              value={provider}
              onChange={(event) => setProvider(event.target.value)}
            >
              {availableProviders.map((value) => (
                <MenuItem key={value} value={value}>{PROVIDERS[value]}</MenuItem>
              ))}
            </TextField>

            <TextField
              className={classes.secret}
              variant="outlined"
              label="Chave secreta"
              type={showSecret ? "text" : "password"}
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              autoComplete="new-password"
              helperText="Cole a chave correspondente ao provedor e à finalidade selecionados."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showSecret ? "Ocultar chave" : "Mostrar chave"}
                      onClick={() => setShowSecret((current) => !current)}
                      edge="end"
                    >
                      {showSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Credenciais configuradas</Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
            ) : credentials.length ? (
              <div className={classes.list}>
                {credentials.map((credential) => (
                  <Paper key={credential.id} className={classes.credential}>
                    <div className={classes.credentialInfo}>
                      <Typography variant="body2">
                        {PROVIDERS[credential.provider] || credential.provider} · {purposeLabel(credential.purpose)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {credential.secretHint || "configurada"}
                      </Typography>
                    </div>
                    <Chip
                      size="small"
                      color={credential.status === "active" ? "primary" : "default"}
                      label={credential.status === "active" ? "Ativa" : "Inativa"}
                    />
                  </Paper>
                ))}
              </div>
            ) : (
              <Paper className={classes.empty} variant="outlined">
                <Typography variant="body2" color="textSecondary">
                  Nenhuma credencial configurada para este tenant.
                </Typography>
              </Paper>
            )}
          </Box>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>Fechar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={save}
          disabled={saving || secret.trim().length < 8}
        >
          {saving ? <CircularProgress size={22} /> : "Salvar credencial"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiCredentialsModal;
